// @flow
import React from 'react'
import I18n from 'i18n-react'
import classNames from 'classnames'
import { humanizer } from 'devx-js-utilities'

// core
import { getValueForPath, getYearPrice } from '../FilterFunctions'

// components
import quoteComponents from '../../Components/_quoteComponents/quoteComponents'
import { SupplementaryInsurances } from '../../Components/'

// types
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type { ValidationErrorType } from '../Types/ValidationError'
import type { ProductContent } from '../Types/ProductContent'
import type { InputHelpsType } from '../Types/InputHelpType'
import type { ValueObject } from '../Types/Values'
import type { ColumnConfigType } from '../Types/ConfigTypes'
import type { ColumnsConfigType } from '../Types/ConfigTypes'
import type { InsuranceCompaniesType } from '../Types/InsuranceCompanyTypes'
import type { OneQuoteDocuments } from '../Types/DocumentTypes'
import type { CoveragePriceType } from '../Redux/Types/QuoteStateType'
import type { ActiveSettingsType, IdentifyObject } from '../Types/SettingsTypes'
import type { AsMutable } from '../Types/ImmutableTypes'
import type QuoteInstance from '../Controllers/QuoteInstance'

type QuoteProps = {
  activeQuoteKey: string | Array<string>,
  activeSettings: ActiveSettingsType & AsMutable<ActiveSettingsType>,
  basePrices: { [tariffName: string]: number },
  bodyAdditionalRowConfig: ColumnsConfigType,
  bodyConfig: ColumnsConfigType,
  content: ProductContent,
  coverages: any,
  debug?: boolean,
  documents: OneQuoteDocuments,
  identifyObject?: IdentifyObject,
  inEmailQuote?: boolean,
  inputHelps?: InputHelpsType,
  insuranceCompanies: InsuranceCompaniesType,
  isBackOffice: boolean,
  onChangeSupplementaryInsurance: (key: string, value: string | Object, pathArray: Array<string>) => void,
  onChangeSupplementaryInsurances: (data: Object, paths: Array<string>) => void,
  onOpenSuriIndex: () => void,
  onQuoteValidate: () => void,
  onRefresh: (quoteId: string) => void,
  onRemoveSupplementaryInsurance: (string) => void,
  onRenderColumnValue: (ColumnConfigType, QuoteInstance, Object, number, InsuranceCompaniesType) => void,
  onShowPriceFor: (string) => void,
  onSubmit: (quote: QuoteInstance) => void,
  priceWithCoverages?: CoveragePriceType,
  pushIsPristine: boolean,
  quote: QuoteInstance,
  seats?: string,
  selectedQuote: QuoteInstance,
  showAdditionalRow?: boolean,
  showAddToCard: boolean,
  showBottomCart: boolean,
  showCallback: boolean,
  showPerUnitOfQuality?: boolean,
  showPrice: boolean,
  showPriceKey: string,
  showPriceWithCoverages: boolean,
  showSupplementaryInsurances: boolean,
  specification?: ExpandedSpecificationDTO,
  supplementaryInsurances: { [key: string]: any },
  templateVariables?: ValueObject,
  token?: string,
  validationErrors: ValidationErrorType,
  valueNames: { [key: string]: { [key: string]: string } },
  webType: number,
}

type QuoteState = {
  collapsed: boolean,
  showHelp: boolean,
  additionalShowHelp: boolean,
}

export default class Quote extends React.Component<QuoteProps, QuoteState> {
  static defaultProps = {
    content: null,
    debug: false,
    inputHelps: {},
    showPriceWithCoverages: false,
    specification: null,
    templateVariables: {},
    showPerUnitOfQuality: true,
  }

  state = {
    collapsed: true,
    showHelp: false,
    additionalShowHelp: false,
  }

  shouldComponentUpdate(nextProps: QuoteProps, nextState: QuoteState) {
    return (
      JSON.stringify([
        nextProps.activeQuoteKey,
        nextProps.basePrices,
        nextProps.documents,
        nextProps.insuranceCompanies,
        nextProps.showPriceWithCoverages,
        nextProps.priceWithCoverages,
        nextProps.quote,
        nextProps.showPrice,
        nextProps.showPriceKey,
        nextProps.templateVariables,
        nextProps.inEmailQuote,
      ]) !==
        JSON.stringify([
          this.props.activeQuoteKey,
          this.props.basePrices,
          this.props.documents,
          this.props.insuranceCompanies,
          this.props.showPriceWithCoverages,
          this.props.priceWithCoverages,
          this.props.quote,
          this.props.showPrice,
          this.props.showPriceKey,
          this.props.templateVariables,
          this.props.inEmailQuote,
        ]) ||
      this.state.showHelp !== nextState.showHelp ||
      this.state.additionalShowHelp !== nextState.additionalShowHelp ||
      this.props.seats !== nextProps.seats ||
      (nextProps.isBackOffice &&
        !nextState.collapsed &&
        JSON.stringify([
          nextProps.content,
          nextProps.coverages,
          nextProps.inputHelps,
          nextProps.pushIsPristine,
          nextProps.specification,
          nextProps.supplementaryInsurances,
          nextProps.valueNames,
        ]) !==
          JSON.stringify([
            this.props.content,
            this.props.coverages,
            this.props.inputHelps,
            this.props.pushIsPristine,
            this.props.specification,
            this.props.supplementaryInsurances,
            this.props.valueNames,
          ])) ||
      this.state.collapsed !== nextState.collapsed ||
      (JSON.stringify(nextProps.selectedQuote) !== JSON.stringify(this.props.selectedQuote) &&
        (`${nextProps.selectedQuote.uniqueQuoteId}`.includes(nextProps.quote.uniqueQuoteId) ||
          `${this.props.selectedQuote.uniqueQuoteId}`.includes(nextProps.quote.uniqueQuoteId)))
    )
  }

  handleSubmit = () => {
    this.props.onSubmit(this.props.quote)
  }

  handleRefresh = () => {
    this.props.onRefresh(this.props.quote.id)
  }

  handleShowCallback = () => {
    this.props.onShowCallback(this.props.quote.id)
  }

  handleToggleCollapse = () => {
    const collapsed = !this.state.collapsed
    this.setState({ collapsed })
    if (!collapsed) {
      this.props.onQuoteValidate()
      if (this.props.seats) {
        this.handleOnChange('seats', this.props.seats, ['seats'])
      }
    }
  }

  handleOnChange = (key: string, value: string | Object, pathArray: Array<string>) => {
    this.props.onChangeSupplementaryInsurance(key, value, pathArray)
  }

  handleRemoveSupplementaryInsurance = (id: string) => {
    this.props.onRemoveSupplementaryInsurance(id)
  }

  handleChangeSupplementaryInsurances = (data: Object, paths: Array<string>) => {
    this.props.onChangeSupplementaryInsurances(data, paths)
  }

  handleGetCoveragePricesValidate = (data: Object) => {
    this.props.onRefreshCoveragePrices(data)
  }

  handleOnCloseHelp = (additionalRow?: boolean) => {
    this.setState({ [additionalRow ? 'additionalShowHelp' : 'showHelp']: false })
  }

  handleOnOpenHelp = (additionalRow?: boolean) => {
    this.setState({ [additionalRow ? 'additionalShowHelp' : 'showHelp']: true })
  }

  getQuotePrice = (quote: QuoteInstance) => {
    const { basePrices, activeQuoteKey } = this.props

    if (Object.keys(quote.subProductToTariffName).length === 1) {
      return quote.price
    }
    if (!Array.isArray(activeQuoteKey)) {
      if (basePrices[quote.subProductToTariffName.tpl]) {
        if (activeQuoteKey === 'tpl') {
          return basePrices[quote.subProductToTariffName.tpl]
        } else {
          return quote.price - basePrices[quote.subProductToTariffName.tpl]
        }
      }
      if (basePrices[quote.subProductToTariffName.cic]) {
        if (activeQuoteKey === 'cic') {
          return basePrices[quote.subProductToTariffName.cic]
        } else {
          return quote.price - basePrices[quote.subProductToTariffName.cic]
        }
      }
    } else {
      return quote.price
    }
    return quote.price
  }

  handleAddToCard = () => {
    this.props.onAdd(this.props.quote)
  }

  handleShowPriceFor = () => {
    const { showPriceKey, onShowPriceFor, quote } = this.props
    if (quote.insuranceCompany) {
      onShowPriceFor(showPriceKey)
    }
  }

  renderTypeSubmit = (columnConfig: ColumnConfigType, quote: QuoteInstance) => {
    const {
      showPrice,
      showAddToCard,
      showCallback,
      debug,
      showSupplementaryInsurances,
      showPriceWithCoverages,
      priceWithCoverages,
      selectedQuote,
      showBottomCart,
      paymentPeriod,
      inEmailQuote,
    } = this.props
    let isPickedQuote = false
    if (
      showBottomCart &&
      (selectedQuote.uniqueQuoteId === quote.uniqueQuoteId ||
        (selectedQuote.subProductToTariffName &&
          Object.keys(selectedQuote.subProductToTariffName).length > 1 &&
          `${selectedQuote.uniqueQuoteId}`.includes(quote.uniqueQuoteId)))
    ) {
      isPickedQuote = true
    }
    const buttonClasses = [
      'btn btn-secondary px-3 w-100',
      {
        'mr-2': showAddToCard,
        disabled: isPickedQuote,
      },
    ]
    let price = this.getQuotePrice(quote)
    if (price < 0) {
      price = quote.price
    }
    let priceBefore = price
    if (showPriceWithCoverages && priceWithCoverages && priceWithCoverages.fullPrice !== price) {
      price = priceWithCoverages.fullPrice
    }

    let priceText = `${humanizer.csNumber(price)} ${I18n.translate('general.currency')}`
    if (paymentPeriod && paymentPeriod !== 'annually') {
      priceText += ` ${I18n.translate(`general.perPeriod.${paymentPeriod}`)}`
      if (paymentPeriod !== 'annually') {
        let pricePerYear = getYearPrice(paymentPeriod, price)
        priceText = I18n.translate('general.priceTariff', {
          priceTariff: priceText,
          pricePerYear: `${humanizer.csNumber(pricePerYear)} ${I18n.translate('general.currency')}`,
        })
      }
    }

    if (showCallback) {
      priceText = I18n.translate('general.bonusMalusPrice', {
        priceFrom: `${humanizer.csNumber(price)} ${I18n.translate('general.currency')}`,
        priceTo: `${humanizer.csNumber(price * 2)} ${I18n.translate('general.currency')}`,
        suffix: <small>{I18n.translate('general.bonusMalusPriceSuffix')}</small>,
      })
    }

    return [
      <span
        className="lead h3-lg align-middle px-3 px-lg-0 d-block mb-1 mt-xxl-1 text-nowrap"
        key={`quote-insuranceCompany-submit-${columnConfig.key}-${quote.id}`}
      >
        {showPrice && [
          showPriceWithCoverages && [
            <span className="text-muted small" key="quote-old-price">
              <s>{`${humanizer.csNumber(priceBefore)} ${I18n.translate('general.currency')}`}</s>
            </span>,
            <br key="quote-price-br" />,
            <small key="quote-coverages-title">{I18n.translate('general.withCoverages')}</small>,
            <br key="quote-price-br-2" />,
          ],
          <span key="quote-price">{priceText}</span>,
          debug && <span className="text-secondary">&nbsp;{quote.price}&nbsp;Kc</span>,
        ]}
      </span>,
      <div
        className="d-flex justify-content-center flex-wrap"
        key={`quote-insuranceCompany-submit-actions-${columnConfig.key}-${quote.id}`}
      >
        <div className="d-flex mb-2 w-100">
          {showCallback ? (
            <button className={classNames(buttonClasses)} onClick={this.handleShowCallback}>
              {I18n.translate('general.showQuoteCallback')}
            </button>
          ) : showPrice || isPickedQuote ? (
            <button className={classNames(buttonClasses)} onClick={isPickedQuote ? () => {} : this.handleSubmit}>
              {isPickedQuote ? I18n.translate('general.chosen') : I18n.translate('general.pickVariant')}
            </button>
          ) : (
            <button className={classNames(buttonClasses)} onClick={this.handleShowPriceFor}>
              {I18n.translate('general.showPrice')}
            </button>
          )}
          {showAddToCard && (
            <button
              className={`btn ${inEmailQuote ? 'btn-secondary' : 'btn-default'} px-3`}
              onClick={this.handleAddToCard}
              type="button"
            >
              {inEmailQuote ? <span>-</span> : <span>+</span>}
            </button>
          )}
        </div>
        {showSupplementaryInsurances && (
          <button className="btn btn-dark px-3 w-100" onClick={this.handleToggleCollapse}>
            {I18n.translate('general.supplementaryInsurance')}
          </button>
        )}
      </div>,
    ]
  }

  renderColumnValue = (columnConfig: ColumnConfigType, additionalRow?: boolean) => {
    const {
      quote,
      webType,
      insuranceCompanies,
      documents,
      templateVariables,
      showPerUnitOfQuality,
      showPrice,
      identifyObject,
    } = this.props

    let ColumnComponent = quoteComponents.columnTypeDefault
    if (quoteComponents[columnConfig.type]) {
      ColumnComponent = quoteComponents[columnConfig.type]
    }
    const data = getValueForPath(columnConfig.path, 0, { ...quote })
    if (this.props.onRenderColumnValue && typeof this.props.onRenderColumnValue === 'function') {
      return this.props.onRenderColumnValue(columnConfig, quote, data, webType, insuranceCompanies)
    }

    if (columnConfig.type === 'submit') {
      return this.renderTypeSubmit(columnConfig, quote)
    }

    return (
      <ColumnComponent
        additionalRow={additionalRow}
        columnConfig={columnConfig}
        data={data !== null ? data : {}}
        documents={documents}
        identifyObject={identifyObject}
        insuranceCompanies={insuranceCompanies}
        onCloseHelp={() => {
          this.handleOnCloseHelp(additionalRow)
        }}
        onOpenHelp={() => {
          this.handleOnOpenHelp(additionalRow)
        }}
        onOpenSuriIndex={this.props.onOpenSuriIndex}
        quote={quote}
        showHelp={additionalRow ? this.state.additionalShowHelp : this.state.showHelp}
        showPerUnitOfQuality={showPerUnitOfQuality}
        showPrice={showPrice}
        templateVariables={templateVariables}
        webType={webType}
      />
    )
  }

  render() {
    const {
      quote,
      bodyConfig,
      showSupplementaryInsurances,
      specification,
      content,
      debug,
      inputHelps,
      pushIsPristine,
      supplementaryInsurances,
      templateVariables,
      validationErrors,
      valueNames,
      resources,
      coverages,
      bodyAdditionalRowConfig,
      showAdditionalRow,
      documents,
      isBackOffice,
      token,
      identifyObject,
      activeSettings,
    } = this.props

    return [
      <tr className="d-block d-lg-table-row border-top" key={quote.id}>
        {bodyConfig.map((columnConfig) => [
          <td
            className={columnConfig.className}
            colSpan={columnConfig.colSpan || 1}
            id={`column-${quote.id}-${columnConfig.key}`}
            key={columnConfig.key}
            rowSpan={columnConfig.rowSpan || 1}
          >
            {this.renderColumnValue(columnConfig)}
          </td>,
        ])}
      </tr>,
      showAdditionalRow && (
        <tr className="d-block d-lg-table-row border-top border-top-light" key={`${quote.id}-additional`}>
          {bodyAdditionalRowConfig.map((columnConfig) => [
            <td
              className={columnConfig.className}
              colSpan={columnConfig.colSpan || 1}
              key={`${columnConfig.key}-additional`}
              rowSpan={columnConfig.rowSpan || 1}
            >
              {this.renderColumnValue(columnConfig, true)}
            </td>,
          ])}
        </tr>
      ),
      showSupplementaryInsurances && specification && specification.fields ? (
        <tr
          className={classNames([!this.state.collapsed ? 'd-block d-lg-table-row' : 'd-none'])}
          key={`insurances-${quote.id}`}
        >
          <td className="border-top" colSpan={bodyConfig.length}>
            <SupplementaryInsurances
              activeSettings={activeSettings}
              calculatorName={quote.calculatorName}
              content={content}
              debug={debug}
              documents={documents}
              hideDisabled
              identifyObject={identifyObject}
              inputHelps={inputHelps}
              isBackOffice={isBackOffice}
              narrow
              onChangeSupplementaryInsurances={this.handleChangeSupplementaryInsurances}
              onChangeUserFormData={this.props.onChangeUserFormData}
              onChangeValue={this.handleOnChange}
              onQuoteValidate={this.props.onQuoteValidate}
              onRefreshCoveragePrices={this.handleGetCoveragePricesValidate}
              onRemoveSupplementaryInsurance={this.handleRemoveSupplementaryInsurance}
              pushIsPristine={pushIsPristine}
              quoteId={quote.id}
              resources={{
                ...resources,
                coverages,
              }}
              specification={specification}
              supplementaryInsurances={supplementaryInsurances}
              tariffContent={quote.tariffContent}
              templateVariables={templateVariables}
              token={token}
              validationErrors={validationErrors}
              valueNames={valueNames}
            />
          </td>
        </tr>
      ) : null,
    ]
  }
}
